import React, { useState, useEffect, useCallback } from 'react';
import { Shield, UserPlus, Trash2, Crown, RefreshCw, AlertCircle, Check, X } from 'lucide-react';

const AdminSettings = ({ user }) => {
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchAdminList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${backendUrl}/api/admin/settings/admins`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to fetch admin list');
      }
      
      console.log('Admin settings data:', data); // Debug log
      setAdminData(data);
    } catch (err) {
      console.error('Error fetching admin list:', err);
      setError(err.message || 'Failed to load admin list');
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchAdminList();
  }, [fetchAdminList]);

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;

    setAdding(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/settings/admins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newEmail.trim().toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to add email');
      }

      setAdminData(prev => ({ ...prev, approved_emails: data.approved_emails }));
      setNewEmail('');
      setSuccess(`${newEmail} has been approved for admin access`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    setRemoving(email);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${backendUrl}/api/admin/settings/admins/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to remove email');
      }

      setAdminData(prev => ({ ...prev, approved_emails: data.approved_emails }));
      setSuccess(`${email} has been removed from admin access`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  const isPrimary = adminData?.is_primary;
  const approvedEmails = adminData?.approved_emails || [];
  const primaryAdmin = adminData?.primary_admin;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-violet-100 rounded-lg">
            <Shield className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-stone-900">Access Controls</h2>
            <p className="text-sm text-stone-500">Manage who can access the admin panel</p>
          </div>
        </div>
        <button
          onClick={fetchAdminList}
          className="p-2 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Current User Info */}
      <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
        <p className="text-sm text-stone-600">
          Logged in as: <span className="font-medium text-stone-900">{user?.email || 'Unknown'}</span>
          {isPrimary && <span className="ml-2 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Owner</span>}
        </p>
      </div>

      {/* Primary Admin Notice (for non-primary admins) */}
      {!isPrimary && primaryAdmin && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <Crown className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">View Only</p>
            <p className="text-xs text-amber-600 mt-1">
              Only the primary admin ({primaryAdmin}) can add or remove admin access.
            </p>
          </div>
        </div>
      )}

      {/* Add New Admin (Primary Admin Only) */}
      {isPrimary && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-200">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-teal-600" />
            Add Admin Access
          </h3>
          <form onSubmit={handleAddEmail} className="flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email address"
              className="flex-1 px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              data-testid="add-admin-email-input"
            />
            <button
              type="submit"
              disabled={adding || !newEmail.trim()}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="add-admin-btn"
            >
              {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Add
            </button>
          </form>
          <p className="text-xs text-stone-400 mt-2">
            Added users will be able to access the admin panel after logging in with Google.
          </p>
        </div>
      )}

      {/* Approved Admins List */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-200">
        <h3 className="font-semibold text-stone-900 mb-4">
          Approved Admins ({approvedEmails.length})
        </h3>
        <div className="space-y-2">
          {approvedEmails.length > 0 ? (
            approvedEmails.map((email) => {
              const isPrimaryEmail = email === primaryAdmin;
              const isCurrentUser = email === user?.email;
              
              return (
                <div
                  key={email}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isPrimaryEmail ? 'bg-violet-50 border border-violet-100' : 'bg-stone-50'
                  }`}
                  data-testid={`admin-email-${email}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isPrimaryEmail ? 'bg-violet-200 text-violet-700' : 'bg-stone-200 text-stone-600'
                    }`}>
                      {email[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-stone-900 flex items-center gap-2">
                        {email}
                        {isPrimaryEmail && (
                          <span className="inline-flex items-center gap-1 text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
                            <Crown className="w-3 h-3" />
                            Owner
                          </span>
                        )}
                        {isCurrentUser && !isPrimaryEmail && (
                          <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                            You
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {isPrimary && !isPrimaryEmail && (
                    <button
                      onClick={() => handleRemoveEmail(email)}
                      disabled={removing === email}
                      className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Remove admin access"
                      data-testid={`remove-admin-${email}`}
                    >
                      {removing === email ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-6">
              <p className="text-stone-400 text-sm">No approved admins found</p>
              <p className="text-stone-400 text-xs mt-1">
                {error ? 'There was an error loading the list.' : 'Try refreshing the page.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
        <p className="text-xs text-stone-500">
          <strong>How it works:</strong> Only approved email addresses can access this admin panel. 
          When someone tries to log in, their Google account email is checked against this list. 
          The primary admin (owner) cannot be removed and is the only one who can manage this list.
        </p>
      </div>
    </div>
  );
};

export default AdminSettings;
